import { useEffect, useRef, useState } from 'react';

const ESTIMATED_MS_PER_ITEM = 2000;
const TICK_INTERVAL_MS = 100;
const COMPLETION_DELAY_MS = 500;

interface UseAssessmentProgressOptions {
  /** Total items in the batch (0 = no batch) */
  batchSize: number;
  /** Items completed according to server */
  batchCompleted: number;
}

interface UseAssessmentProgressReturn {
  /** Whether to show the progress bar */
  showProgress: boolean;
  /** Current progress value (0-100) */
  progress: number;
}

/**
 * Smooth progress bar for batch impact assessments.
 *
 * Estimates progress locally assuming ~2s per item, then reconciles
 * with server data when polled. If the server is ahead, snaps forward.
 * If behind, keeps the local estimate (never goes backward).
 * When server reports batch done (batchSize=0), shows 100% briefly then hides.
 */
export const useAssessmentProgress = ({
  batchSize,
  batchCompleted,
}: UseAssessmentProgressOptions): UseAssessmentProgressReturn => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  const wasActiveRef = useRef(false);
  const startTimeRef = useRef<number | null>(null);
  const serverProgressRef = useRef(0);

  const isActive = batchSize > 0;
  const serverProgress = batchSize > 0 ? (batchCompleted / batchSize) * 100 : 0;

  // Track latest server progress for the tick interval
  serverProgressRef.current = serverProgress;

  // Handle batch start / end transitions
  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      // Batch just started
      wasActiveRef.current = true;
      startTimeRef.current = Date.now();
      setShowProgress(true);
      setDisplayProgress(serverProgress);
    }

    if (!isActive && wasActiveRef.current) {
      // Batch just finished — show 100% briefly then hide
      wasActiveRef.current = false;
      setDisplayProgress(100);
      const timeout = setTimeout(() => {
        setShowProgress(false);
        setDisplayProgress(0);
        startTimeRef.current = null;
      }, COMPLETION_DELAY_MS);
      return () => clearTimeout(timeout);
    }
  }, [isActive, serverProgress]);

  // Track when we last received a server update for smooth interpolation
  const lastServerUpdateRef = useRef<number>(Date.now());
  const lastServerProgressRef = useRef(0);

  useEffect(() => {
    if (isActive && serverProgress !== lastServerProgressRef.current) {
      lastServerUpdateRef.current = Date.now();
      lastServerProgressRef.current = serverProgress;
    }
  }, [isActive, serverProgress]);

  // Smooth local tick while batch is active
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setDisplayProgress(prev => {
        if (batchSize <= 0) return prev;

        const server = serverProgressRef.current;

        // Smoothly interpolate between polls: estimate progress based on
        // time since last server update, assuming ~2s per item
        const elapsed = Date.now() - lastServerUpdateRef.current;
        const itemProgressSinceUpdate = elapsed / ESTIMATED_MS_PER_ITEM;
        const percentPerItem = 100 / batchSize;
        const interpolated = server + itemProgressSinceUpdate * percentPerItem;

        // Use the max of: server truth, interpolated, previous (never go backward)
        // But cap interpolation to not exceed server + 5% to avoid outracing
        const target = Math.max(
          server,
          Math.min(interpolated, server + 5),
          prev
        );

        // Cap at 99% — only server completion (batchSize=0) triggers 100%
        return Math.min(target, 99);
      });
    }, TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isActive, batchSize]);

  return { showProgress, progress: displayProgress };
};
